import React, { useRef, useEffect, useState, useMemo } from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { X, Loader2 } from "lucide-react";
import { PositionChart } from "@/components/PositionChart";
import {
  TradingViewChartWidget,
  useTradingViewSymbol,
} from "@crypto-trading/ui-shared/client";
import { useAlertPriceData } from "@/hooks/useAlertPriceData";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { AlertDetailSummary, AlertDetailTitle } from "@/components/alerts/AlertDetailSummary";
import { TokenAnalysisSection } from "@/components/TokenAnalysisSection";
import { trackEvent } from "@/utils/analytics";
import { useTokenAvailability } from "@/hooks/useMarketData";
import { useUserExchanges } from "@/hooks/useExchanges";
import { ensureUsdtSymbol } from "@/utils/symbols";
import { usePreviousAlerts } from "@/hooks/useCryptoAlerts";
import { AlertHistory } from "@/components/AlertHistory";

interface AlertDetailDrawerDesktopProps {
  userId: string;
  alert: CryptoAlertData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTrade?: (alert: CryptoAlertData) => void;
  onViewPositions?: (alert: CryptoAlertData) => void;
}

const SHEET_WIDTH = 500;

export const AlertDetailDrawerDesktop: React.FC<
  AlertDetailDrawerDesktopProps
> = ({ userId, alert, open, onOpenChange, onTrade, onViewPositions }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);
  const [isLivePrice, setIsLivePrice] = useState(false);

  // Animation states
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);


  // Normalize symbol for token availability check
  const normalizedSymbol = useMemo(
    () => (alert?.currency ? ensureUsdtSymbol(alert.currency) : ""),
    [alert?.currency]
  );

  // Check token availability and user exchanges
  const { data: exchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId, true);
  const connectedExchangeCodes = useMemo(
    () => exchanges.map((e) => e.code),
    [exchanges]
  );
  const { data: tokenAvailability, isLoading: isLoadingAvailability } =
    useTokenAvailability(userId, normalizedSymbol, open && !!normalizedSymbol);

  const globallyAvailableExchangeCodes = useMemo(
    () => tokenAvailability?.userExchanges ?? [],
    [tokenAvailability?.userExchanges]
  );
  const globallyAvailableSet = useMemo(
    () => new Set(globallyAvailableExchangeCodes),
    [globallyAvailableExchangeCodes]
  );
  const supportedUserExchangeCodes = useMemo(
    () =>
      exchanges
        .map((e) => e.code)
        .filter((code) => globallyAvailableSet.has(code)),
    [exchanges, globallyAvailableSet]
  );
  const hasTradableExchange = supportedUserExchangeCodes.length > 0;
  const isCheckingAvailability = isLoadingAvailability || isLoadingExchanges;

  // Fetch previous alerts
  const { data: previousAlerts = [] } = usePreviousAlerts(
    userId,
    alert?.currency || "",
    alert?.id || "",
    open && !!alert
  );

  // Subscribe to live price updates
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    alert?.currency || "",
    "dynamic",
    open && !!alert // Same as the alert tile: every alert type shows a current price
  );

  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (open && !livePrice) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => setPriceLoadTimeout(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [open, livePrice]);

  const displayPrice = livePrice ?? null;

  const {
    enabledIntervals,
    selectedInterval,
    setSelectedInterval,
    priceChanges,
    isLoading,
    isLoadingMore,
    error: candleError,
    loadOlderCandles,
    loadNewerCandles,
    hasMoreHistory,
    hasMoreFuture,
    setFollowLive,
    symbol,
    isTokenUnavailable,
    historicalChartUnavailable,
  } = useAlertPriceData({ userId, alert, open });

  const isPriceLoading =
    open && !livePrice && !priceLoadTimeout && !isTokenUnavailable;

  // A live exchange price is current, so a chart far from it is the wrong
  // token and is hidden. The trigger price can be old, so it only ranks.
  const liveChartOptions = livePrice
    ? { referencePrice: livePrice, rejectAbove: 0.3 }
    : { referencePrice: alert?.price ?? null };

  const {
    tradingViewSymbol,
    isLoading: isResolvingLiveChart,
    isError: isLiveChartMissing,
  } = useTradingViewSymbol(symbol, liveChartOptions);
  const liveChartReady = Boolean(tradingViewSymbol) && !isLiveChartMissing;
  const showIntervalChart =
    !historicalChartUnavailable &&
    (isLoading || priceChanges.length > 0 || Boolean(candleError));

  useEffect(() => {
    if (!open) {
      setIsLivePrice(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && alert) {
      trackEvent("alert_drawer_view", {
        userId,
        alertId: alert.id,
        symbol: alert.currency,
        alertType: alert.type,
        tier: alert.tier ?? null,
        exchange: alert.exchange,
      });
    }
  }, [open, alert]);

  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPositionRef.current;
        }
      });
    }
  }, [selectedInterval, isLoading, priceChanges.length]);

  // Animation effect
  useEffect(() => {
    if (open && alert) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = "hidden";
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, alert]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onOpenChange(false);
    };
    if (open) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  if (!alert || !isVisible) return null;

  const formatPrice = (price: number | null) => {
    if (!price)
      return isTokenUnavailable
        ? "Not available at supported exchanges"
        : "Not available";
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const calculatePriceChange = () => {
    if (!alert?.price || !displayPrice) return null;
    return ((displayPrice - alert.price) / alert.price) * 100;
  };

  const formatDateTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleString(undefined, {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h24",
    });
  };

  const priceChange = calculatePriceChange();

  return (
    <>
      {/* Overlay with fade animation */}
      <div
        className="fixed inset-0 bg-black/60 z-[100] transition-opacity duration-300"
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />

      {/* Sheet with slide animation */}
      <div
        className="fixed top-0 right-0 h-full bg-primary border-l border-white/10 flex flex-col shadow-2xl z-[101] transition-transform duration-300 ease-out"
        style={{
          width: `${SHEET_WIDTH}px`,
          transform: isAnimating ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex flex-col gap-4 px-6 pb-2 pt-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-medium text-white">Alert Details</h2>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="rounded-lg p-2 text-[#a3a3a3] transition-colors hover:bg-white/5 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <AlertDetailTitle alert={alert} />
        </div>

        {/* Content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 space-y-4 overflow-y-auto px-6 pb-6 pt-2"
        >
          <AlertDetailSummary
            alert={alert}
            livePrice={displayPrice}
            isSubscribed={isSubscribed}
            isPriceLoading={isPriceLoading}
            priceChange={priceChange}
            formatPrice={formatPrice}
            formatDateTime={formatDateTime}
            connectedExchangeCodes={connectedExchangeCodes}
            open={open}
          />

          {/* Price Chart */}
          <div className="glass p-3">
            {showIntervalChart ? (
              isLivePrice && liveChartReady ? (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-bold text-text-primary">
                      Price Chart
                    </h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-text-secondary">Live</span>
                      <div className="relative inline-block w-10 h-5">
                        <input
                          type="checkbox"
                          checked={isLivePrice}
                          onChange={(e) => setIsLivePrice(e.target.checked)}
                          className="sr-only"
                        />
                        <div
                          className={`block w-10 h-5 rounded-full transition-colors ${isLivePrice ? "bg-accent" : "bg-gray-600"}`}
                        />
                        <div
                          className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${isLivePrice ? "translate-x-5" : "translate-x-0"}`}
                        />
                      </div>
                    </label>
                  </div>
                  <TradingViewChartWidget symbol={symbol} {...liveChartOptions} />
                </>
              ) : (
                <PositionChart
                  priceChanges={priceChanges}
                  entryPrice={alert.price}
                  isProfitable={
                    alert.userPosition?.pnlPercentage !== null &&
                    alert.userPosition?.pnlPercentage !== undefined &&
                    alert.userPosition.pnlPercentage >= 0
                  }
                  openedAt={alert.createdAt.toString()}
                  markerText="Triggered"
                  entryMarkerVariant="callout"
                  onRequestOlderCandles={loadOlderCandles}
                  onRequestNewerCandles={loadNewerCandles}
                  hasMoreHistory={hasMoreHistory}
                  hasMoreFuture={hasMoreFuture}
                  onFollowLiveChange={setFollowLive}
                  viewportResetKey={`${selectedInterval}-${alert?.id ?? ""}`}
                  enabledIntervals={
                    priceChanges.length > 0 ? enabledIntervals : []
                  }
                  selectedInterval={selectedInterval}
                  onIntervalChange={setSelectedInterval}
                  isLoading={isLoading}
                  isLoadingMore={isLoadingMore}
                  error={candleError}
                  headerActions={
                    liveChartReady ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-text-secondary">Live</span>
                        <div className="relative inline-block w-10 h-5">
                          <input
                            type="checkbox"
                            checked={isLivePrice}
                            onChange={(e) => setIsLivePrice(e.target.checked)}
                            className="sr-only"
                          />
                          <div
                            className={`block w-10 h-5 rounded-full transition-colors ${isLivePrice ? "bg-accent" : "bg-gray-600"}`}
                          />
                          <div
                            className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${isLivePrice ? "translate-x-5" : "translate-x-0"}`}
                          />
                        </div>
                      </label>
                    ) : undefined
                  }
                />
              )
            ) : isResolvingLiveChart ? (
              <>
                <h3 className="text-sm font-bold text-text-primary mb-3">
                  Price Chart
                </h3>
                <div className="flex items-center justify-center h-[300px] bg-primary/50 rounded-lg">
                  <Loader2 className="w-6 h-6 animate-spin text-accent" />
                </div>
              </>
            ) : liveChartReady ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-text-primary">
                    Price Chart
                  </h3>
                  <span className="text-xs text-text-secondary">Live</span>
                </div>
                <TradingViewChartWidget symbol={symbol} {...liveChartOptions} />
              </>
            ) : null}
          </div>

          {/* Previous Alerts Section */}
          <AlertHistory alerts={previousAlerts} variant="drawer-desktop" />

          {/* Token Metadata Section */}
          {alert.tokenMetadata &&
            (alert.tokenMetadata.description || alert.tokenMetadata.urls) && (
              <div className="glass space-y-3 p-3">
                <h3 className="text-sm font-bold text-text-primary">
                  Token Information
                </h3>
                {alert.tokenMetadata.description && (
                  <div>
                    <p className="text-xs text-text-secondary mb-1">
                      Description
                    </p>
                    <p className="text-sm text-text-primary leading-relaxed">
                      {alert.tokenMetadata.description}
                    </p>
                  </div>
                )}
                {alert.tokenMetadata.urls && (
                  <div>
                    <p className="text-xs text-text-secondary mb-2">
                      Official Links
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {alert.tokenMetadata.urls.website?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.website[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          Website
                        </a>
                      )}
                      {alert.tokenMetadata.urls.twitter?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.twitter[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          Twitter
                        </a>
                      )}
                      {alert.tokenMetadata.urls.source_code?.[0] && (
                        <a
                          href={alert.tokenMetadata.urls.source_code[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md bg-primary/60 text-xs text-accent hover:bg-primary/80 transition-colors"
                        >
                          GitHub
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

          <TokenAnalysisSection tokenSymbol={alert.currency} />
        </div>

        {/* Footer Actions */}
        {(alert.userPosition ||
          (onTrade && (hasTradableExchange || isCheckingAvailability))) && (
          <div className="sticky bottom-0 space-y-2 border-t border-white/10 bg-primary/90 p-4 backdrop-blur-md">
            {onTrade && hasTradableExchange && !alert.userPosition && (
              <button
                type="button"
                onClick={() => onTrade(alert)}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent py-3 font-semibold text-white transition-colors hover:bg-accent-hover"
              >
                Trade
              </button>
            )}
            {alert.userPosition && (
              <button
                type="button"
                onClick={() => onViewPositions?.(alert)}
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-accent py-3 font-semibold text-white transition-colors hover:bg-accent-hover"
              >
                View positions
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};
